import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Receipt, TriangleAlert } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { formatUsd } from '@/lib/money'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState, Modal, Select, Tabs } from '@/components/ui/primitives'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { ReverseButton, ReverseDialog, ReversedBadge } from '@/components/ReverseDialog'
import { SettleDialog, type Settleable } from '@/components/SettleDialog'
import { ListFilters, Pager, NO_FILTERS, type ListFilterState } from '@/components/ListControls'
import { usePagedList } from '@/lib/paging'
import { t } from '@/i18n'

interface ExpenseRow {
  id: number
  description: string
  categoryName: string
  amountUsd: number
  amountLbp: number
  paidFrom: string
  expenseDate: string
  dueDate: string | null
  isPaid: boolean
  supplierName: string | null
  reference: string | null
  isReversed: boolean
  reversalReason: string | null
  reversedBy: string | null
}

interface Category {
  id: number
  name: string
  isNonOperating: boolean
}

type TabValue = 'all' | 'unpaid'

const PAID_FROM = [
  { value: 'CashDrawer', label: t('Cash from the till') },
  { value: 'Safe', label: t('Cash from the safe') },
  { value: 'Bank', label: t('Bank transfer') },
  { value: 'Card', label: t('Card') },
  { value: 'Credit', label: t('On credit — not paid yet') },
]

export function ExpensesPage() {
  const queryClient = useQueryClient()
  const canRecord = useSession((state) => state.can)(Permission.RecordExpense)
  const branchId = useSession((state) => state.session)?.branchId ?? null

  const [tab, setTab] = useState<TabValue>('all')
  const [filters, setFilters] = useState<ListFilterState>(NO_FILTERS)
  const [isOpen, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** The unpaid expense being settled, if any. */
  const [settling, setSettling] = useState<Settleable | null>(null)

  const [categoryId, setCategoryId] = useState<number | ''>('')
  const [description, setDescription] = useState('')
  const [usd, setUsd] = useState('')
  const [lbp, setLbp] = useState('')
  const [paidFrom, setPaidFrom] = useState('CashDrawer')
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [dueDate, setDueDate] = useState('')
  const [selectedBranch, setSelectedBranch] = useState<number | ''>(branchId ?? '')
  /** The expense being undone. */
  const [reversing, setReversing] = useState<ExpenseRow | null>(null)

  const categories = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => apiClient.get<Category[]>('/api/expenses/categories'),
  })

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => apiClient.get<{ id: number; displayName: string }[]>('/api/branches'),
    enabled: branchId === null,
  })

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: () => apiClient.get<{ id: number }[]>('/api/registers'),
  })
  const registerId = registers.data?.[0]?.id

  const shift = useQuery({
    queryKey: ['shift', registerId],
    queryFn: () => apiClient.get<{ id: number } | null>(`/api/shifts/current?registerId=${registerId}`),
    enabled: registerId != null,
  })

  const expenses = usePagedList<ExpenseRow>(['expenses', tab], '/api/expenses', {
    unpaidOnly: tab === 'unpaid',
    from: filters.from,
    to: filters.to,
    search: filters.search,
  }, { enabled: canRecord })

  const save = useMutation({
    mutationFn: () =>
      apiClient.post('/api/expenses', {
        branchId: selectedBranch === '' ? null : Number(selectedBranch),
        expenseCategoryId: Number(categoryId),
        supplierId: null,
        description: description.trim(),
        amountUsd: Number(usd || 0),
        amountLbp: Number(lbp || 0),
        vatUsd: 0,
        paidFrom,
        expenseDate: date,
        dueDate: paidFrom === 'Credit' && dueDate ? dueDate : null,
        // Only a till payment needs a shift; the server rejects it if one is missing.
        shiftId: paidFrom === 'CashDrawer' ? (shift.data?.id ?? null) : null,
        reference: null,
      }),
    onSuccess: async () => {
      setOpen(false)
      setDescription('')
      setUsd('')
      setLbp('')
      await queryClient.invalidateQueries({ queryKey: ['expenses'] })
      await queryClient.invalidateQueries({ queryKey: ['shift-expected'] })
      await queryClient.invalidateQueries({ queryKey: ['safe'] })
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not save.'),
  })


  if (!canRecord) {
    return (
      <div className="pb-page px-4 py-6">
        <EmptyState
          icon={Receipt}
          title={t('No access to expenses')}
          description={t('Ask an administrator for the expenses permission.')}
        />
      </div>
    )
  }

  // Reversed rows stay on screen but are no longer a cost, so they are out of the total —
  // matching the server, which leaves them out of the P&L for the same reason.
  const live = expenses.page.filter((row) => !row.isReversed)
  const total = live.reduce((sum, row) => sum + row.amountUsd, 0)

  return (
    <div className="pb-page mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t('Expenses')}</h1>
          <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
            {t('Money out that is not stock — rent, wages, the generator.')}
          </p>
        </div>
        <Button
          onClick={() => {
            setError(null)
            setCategoryId(categories.data?.[0]?.id ?? '')
            setOpen(true)
          }}
        >
          <Plus className="h-4 w-4" />
          {t('Record expense')}
        </Button>
      </header>

      <div className="mb-4">
        <Tabs
          tabs={[
            { value: 'all' as const, label: t('All') },
            { value: 'unpaid' as const, label: t('To pay') },
          ]}
          value={tab}
          onChange={setTab}
        />
      </div>

      <ListFilters
        value={filters}
        onChange={setFilters}
        searchHint={t('What it was for, or the supplier')}
      />

      {expenses.page.length > 0 && (
        <p className="tabular mb-3 text-sm text-[var(--text-secondary)]">
          {/* Said out loud when there is more to load, because a partial sum under a heading that
              reads like a period total is a figure somebody will act on. */}
          {expenses.hasNextPage
            ? t('{amount} across the {shown} shown', {
                amount: formatUsd(total),
                shown: live.length,
              })
            : `${live.length} ${tab === 'unpaid' ? t('outstanding') : t('recorded')} · `}
          {!expenses.hasNextPage && (
            <span className="font-medium text-[var(--text-primary)]">{formatUsd(total)}</span>
          )}
        </p>
      )}

      {expenses.page.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title={tab === 'unpaid' ? 'Nothing outstanding' : 'No expenses yet'}
          description={
            tab === 'unpaid'
              ? 'Bills recorded on credit appear here until they are settled.'
              : 'Stock purchases are not expenses — they become cost of goods when sold.'
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {expenses.page.map((row) => (
            <li
              key={row.id}
              className="surface-raised flex items-center gap-3 rounded-[var(--radius-card)] border p-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`truncate font-medium ${
                      row.isReversed ? 'text-[var(--text-secondary)] line-through' : ''
                    }`}
                  >
                    {row.description}
                  </span>
                  {row.isReversed ? (
                    <ReversedBadge reason={row.reversalReason} by={row.reversedBy} />
                  ) : (
                    !row.isPaid && <Badge tone="warn">{t('To pay')}</Badge>
                  )}
                </div>
                <p className="truncate text-xs text-[var(--text-secondary)]">
                  {row.categoryName} · {row.expenseDate.slice(0, 10)} · {row.paidFrom}
                  {row.dueDate ? ` · due ${row.dueDate.slice(0, 10)}` : ''}
                  {row.isReversed && row.reversalReason ? ` · reversed: ${row.reversalReason}` : ''}
                </p>
              </div>

              <span
                className={`tabular shrink-0 font-semibold ${
                  row.isReversed ? 'text-[var(--text-secondary)] line-through' : ''
                }`}
              >
                {formatUsd(row.amountUsd)}
              </span>

              {!row.isPaid && !row.isReversed && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setSettling({
                      id: row.id,
                      what: row.description,
                      amountUsd: row.amountUsd,
                    })
                  }
                >
                  {t('Pay it')}
                </Button>
              )}

              <ReverseButton
                isReversed={row.isReversed}
                label={`Reverse ${row.description}`}
                onClick={() => setReversing(row)}
              />
            </li>
          ))}
        </ul>
      )}

      <Pager list={expenses} />

      <ReverseDialog
        kind="expense"
        id={reversing?.id ?? null}
        what={reversing ? `${reversing.description} — ${formatUsd(reversing.amountUsd)} from ${reversing.paidFrom}` : ''}
        onClose={() => setReversing(null)}
        invalidate={['expenses', 'safe', 'shift-expected', 'reports']}
      />

      <Modal
        open={isOpen}
        onClose={() => setOpen(false)}
        title={t('Record an expense')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button
              onClick={() => save.mutate()}
              disabled={save.isPending || !description.trim() || categoryId === ''}
            >
              {t('Save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <SearchableSelect
            label={t('Category')}
            value={categoryId}
            placeholder={t('Choose a category…')}
            options={(categories.data ?? []).map((category) => ({
              value: category.id,
              label: category.name,
              hint: category.isNonOperating ? 'Not an operating cost' : undefined,
            }))}
            onChange={(picked) => setCategoryId(Number(picked))}
          />

          <Input
            label={t('What was it for')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('Generator fuel')}
            required
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label={t('US dollars')}
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={usd}
              onChange={(e) => setUsd(e.target.value)}
              className="tabular"
              placeholder="0.00"
            />
            <Input
              label={t('Lebanese lira')}
              type="number"
              inputMode="numeric"
              min="0"
              value={lbp}
              onChange={(e) => setLbp(e.target.value)}
              className="tabular"
              placeholder="0"
            />
          </div>

          <Select label={t('Paid from')} value={paidFrom} onChange={(e) => setPaidFrom(e.target.value)}>
            {PAID_FROM.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          {paidFrom === 'CashDrawer' && !shift.data && (
            <p className="rounded-lg bg-warn-500/10 px-3 py-2 text-sm text-warn-500">
              <TriangleAlert className="mr-1 inline h-4 w-4" />
              {t('No till is open, so this cannot be paid from the drawer.')}
            </p>
          )}

          {branchId === null && (
            <Select
              label={t('Branch')}
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">{t('Choose a branch…')}</option>
              {branches.data?.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.displayName}
                </option>
              ))}
            </Select>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Input label={t('Date')} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            {paidFrom === 'Credit' && (
              <Input
                label={t('Due date')}
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            )}
          </div>

          {error && (
            <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              {error}
            </p>
          )}
        </div>
      </Modal>

      <SettleDialog
        target={settling}
        endpoint={(id) => `/api/expenses/${id}/paid`}
        invalidate={['expenses']}
        onClose={() => setSettling(null)}
      />
    </div>
  )
}
